import {
  HdGateExaltationOrDetriment,
  HdGateLineTranslation,
  HdGateVoice,
} from '../../hd-gate-info.service';
import { HdPlanetTranslations } from '../../hd-planet-info.service';
import { HdBodygraphSide } from '../../hd-bodygraph.models';
import {
  getHdPlanetDisplayConfigByPlanetId,
  HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON,
  HdFixingState,
  HdPlanetDisplayConfig,
  HdChartPointKey,
  findHdChartPointDisplay,
} from '../../planet-grid/hd-planet-display-config';
import {
  HdBodygraphIconKey,
  HdBodygraphIconVisualisation,
  getHdBodygraphIconFaName,
  getHdBodygraphIconVisualisation,
  getHdGateIconLayout,
} from '../../hd-bodygraph-icons';
import { HD_BODYGRAPH_CHANNELS } from '../../hd-bodygraph-metadata';
import { resolveHdPlanetLineFixing } from '../../hd-line-fixing.utils';

/** Exaltation- of detriment-rij van een lijn, klaar om te renderen. */
export interface HdGateLineDualityViewModel {
  /** Astronomicon-glyph van de planeet, of `''` wanneer geen/onbekende planeet. */
  readonly glyphChar: string;
  /** Vertaalde planeetnaam (hover-tooltip), of `''` wanneer geen/onbekende planeet. */
  readonly planetName: string;
  readonly keynoteWhite: string;
  readonly keynoteBlack: string;
  readonly customInterpretation: string;
  /** Eigen exaltation-/detriment-regel (Mechanics in Motion); `''` wanneer niet geladen/ingevuld. */
  readonly ownInterpretation: string;
}

/** Eén planeet-activatie van een lijn in de geladen chart (klaar om te renderen). */
export interface HdGateLineActivationViewModel {
  /** Astronomicon-glyph van het planeet. */
  readonly glyphChar: string;
  /** Vertaalde planeetnaam (hover-tooltip). */
  readonly planetName: string;
  /** `personality` → zwart glyph, `design` → rood glyph. */
  readonly side: 'personality' | 'design';
  /**
   * Fixatie die deze specifieke activatie veroorzaakt op de lijn:
   * `exaltation`/`detriment`/`both` → toon het bijhorende pijltje achter de
   * activatie; `none` → geen pijltje.
   *
   * Sinds de overgang naar planet-match (i.p.v. Maia `fixing`-metadata) wordt
   * deze waarde afgeleid uit een directe vergelijking tussen de activerende
   * planeet en de exaltation-/detriment-planeet van deze lijn — enkel dan is
   * de activerende planeet de werkelijke oorzaak van de fixatie.
   */
  readonly fixing: HdFixingState;
  readonly transitFixing: HdFixingState;
  readonly origin: HdGateLineActivationOrigin;
}

export type HdGateLineActivationOrigin = 'chart' | 'transit';

/**
 * Informatieve planeet op een lijn (Chiron): GEEN activatie. Ze telt niet mee
 * in {@link HdGateLineViewModel.activations} of fixaties; de lijn blijft
 * "niet geactiveerd" en krijgt enkel een subtiele markering.
 */
export interface HdGateLineInformativePlanetViewModel {
  readonly glyphChar: string;
  readonly planetName: string;
  readonly side: 'personality' | 'design';
}

/** Informatieve positie die de builder op haar lijn markeert (zie {@link HdGateLineInformativePlanetViewModel}). */
export interface HdGateLineInformativePosition {
  /** Chiron of een astrologisch punt (AC, DC, MC, IC, Lilith). */
  readonly planetKey: HdChartPointKey;
  readonly side: 'personality' | 'design';
  readonly line: number;
}

/**
 * Eén oorzaak van een exaltation- of detriment-fixatie op een lijn: de planeet
 * die de fixatie aanzet en in welke poort hij staat. Wanneer `gate === null`
 * staat de planeet in déze poort op déze lijn; bij een ingevulde {@link gate}
 * staat hij in een **harmonische poort** (de andere kant van het kanaal).
 */
export interface HdGateLineFixationCause {
  readonly planetName: string;
  readonly side: 'personality' | 'design';
  /** `null` = deze poort + deze lijn; anders het nummer van de harmonische poort. */
  readonly harmonicGate: number | null;
  readonly origin?: HdGateLineActivationOrigin;
}

/** Eén poort-eigenschap-icoon (rolpoort, drukpoort, angst, …) klaar om te renderen. */
export interface HdGateLineIconViewModel {
  readonly key: HdBodygraphIconKey;
  /** FontAwesome-naam (zonder `fa-` prefix). */
  readonly faName: string;
  /** Vertaalde omschrijving van het icoon (hover-tooltip). */
  readonly label: string;
  readonly visualisation: HdBodygraphIconVisualisation;
}

/** Render-klaar view-model voor één lijn van een poort (gedeeld door lijn- en card-variant). */
export interface HdGateLineViewModel {
  /** Kale poortnummer — o.a. voor de grote achtergrond-fill op de card. */
  readonly gate: number;
  readonly lineNumber: number;
  /** `${gate}.${lineNumber}`, bv. `59.1`. */
  readonly gateLineLabel: string;
  readonly keynote: string;
  readonly roleKeynote: string;
  readonly roleKeynote2: string;
  readonly subText: string;
  /** Basis-`learningAbout` (blauw in de UI) — los van de extended-tekst. */
  readonly learningAbout: string;
  /** Inline vervolg op `learningAbout`; in de card enkel zichtbaar onder "extra info". */
  readonly learningAboutExtended: string;
  /** `learningAbout` + `learningAboutExtended` samengevoegd met één spatie (blauw in de UI). */
  readonly learningAboutText: string;
  /** Eigen lijnbeschrijving (Mechanics in Motion); `''` wanneer niet geladen/ingevuld. */
  readonly ownDescription: string;
  readonly zodiacStart: string;
  readonly zodiacEnd: string;
  readonly exaltation: HdGateLineDualityViewModel | null;
  readonly detriment: HdGateLineDualityViewModel | null;
  /** Poort-eigenschap-iconen (rolpoort, drukpoort, angst, …) — getoond linksonder op de card. */
  readonly icons: readonly HdGateLineIconViewModel[];
  /**
   * Planeten die deze lijn activeren in de geladen chart (zwart = personality,
   * rood = design). Leeg wanneer de lijn niet geactiveerd is of er geen chart-
   * context is. Enkel gebruikt door de horizontale lijn-variant.
   */
  readonly activations: readonly HdGateLineActivationViewModel[];
  /**
   * `true` wanneer de poort ergens in de chart activaties heeft maar déze lijn
   * niet — gebruikt om het lijnnummer lichter te kleuren en het "geen-activatie"-
   * icoon te tonen. `false` bij de card-variant of poorten zonder activaties.
   */
  readonly inactiveInChart: boolean;
  /** Vertaalde hover-tekst voor het "geen-activatie"-icoon. Leeg op de card. */
  readonly noActivationLabel: string;
  /**
   * `true` wanneer minstens één activatie van déze lijn een exaltation-fixatie
   * veroorzaakt (`exaltation` of `both`). Stuurt de default-emphasis van de
   * exaltation-/detriment-rijen aan. `false` op de card (geen chart-context).
   */
  readonly hasExaltationFixing: boolean;
  /** Idem voor detriment-fixatie (`detriment` of `both`). */
  readonly hasDetrimentFixing: boolean;
  /**
   * Oorzaken van een exaltation-fixatie van deze lijn — leeg wanneer geen
   * activatie in déze poort op deze lijn én geen activatie in een harmonische
   * poort overeenkomt met de exaltation-planeet. Stuurt de gekleurde/grijze
   * weergave + tooltip van het exaltation-driehoekje + planeet-glyph aan.
   */
  readonly exaltationCauses: readonly HdGateLineFixationCause[];
  /** Idem voor detriment-fixatie. */
  readonly detrimentCauses: readonly HdGateLineFixationCause[];
  /** Pre-geformatteerde tooltip-tekst voor de exaltation-rij (`''` wanneer geen oorzaken). */
  readonly exaltationCauseTooltip: string;
  /** Pre-geformatteerde tooltip-tekst voor de detriment-rij (`''` wanneer geen oorzaken). */
  readonly detrimentCauseTooltip: string;
  readonly exaltationFixingOrigins?: readonly HdGateLineActivationOrigin[];
  readonly detrimentFixingOrigins?: readonly HdGateLineActivationOrigin[];
  /**
   * Informatieve planeten op deze lijn (Chiron). Zo'n lijn telt als niet
   * geactiveerd ({@link inactiveInChart}) maar draagt een subtiele markering.
   */
  readonly informativePlanets?: readonly HdGateLineInformativePlanetViewModel[];
  /** Hover-tekst van die markering ("informatief, geen activatie"). */
  readonly informativeLabel?: string;
}

export interface BuildHdGateLineViewModelParams {
  readonly gate: number;
  readonly lineNumber: number;
  readonly line: HdGateLineTranslation;
  readonly planetTranslations: Readonly<Partial<Record<HdChartPointKey, HdPlanetTranslations>>>;
  /**
   * Vertaalde labels per bodygraph-icon-key (tooltip-tekst voor de poort-iconen).
   * Optioneel: zonder labels vallen de tooltips terug op de icon-key. De
   * horizontale lijn-variant rendert geen iconen en hoeft dit niet mee te geven.
   */
  readonly iconLabels?: Readonly<Record<HdBodygraphIconKey, string>>;
  /**
   * Stem-type van de poort (voice-poorten). Wordt gebruikt om de `voice`-icon-
   * tooltip te verrijken met de specifieke stem, bv. "Stem - Ik weet | volharden,
   * aandringen / ophouden". `null`/weggelaten → de generieke voice-tooltip blijft.
   */
  readonly voice?: HdGateVoice | null;
  /**
   * Angst-keynotes + levensthema van de poort (zie
   * `HdGateInfoService.fearIconDetailIn`), achter de tooltip van de
   * angst-iconen. Leeg/weggelaten → de generieke angst-tooltip blijft.
   */
  readonly fearDetail?: string;
  /**
   * Planeet-activaties van dezelfde poort in de geladen chart (alle lijnen). De
   * builder filtert hieruit de activaties van de huidige lijn en bepaalt of de
   * poort überhaupt activaties heeft (voor `inactiveInChart`). Weggelaten op de
   * card-variant → geen activatie-rendering.
   */
  readonly gateActivations?: readonly HdGateLineActivationSource[];
  /**
   * Planeet-activaties in de **harmonische poorten** (de andere kant van elk
   * kanaal waar deze poort deel van uitmaakt). Wordt gebruikt om exaltation-/
   * detriment-fixaties te detecteren die niet door déze poort zelf veroorzaakt
   * worden maar door dezelfde planeet in de harmonische poort.
   *
   * Weggelaten → geen harmonische detectie (bv. wanneer er geen chart-context
   * is, of bij gebruik buiten de editor).
   */
  readonly harmonicGatesActivations?: readonly HdGateLineActivationSource[];
  /** Vertaalde hover-tekst voor het "geen-activatie"-icoon (lijn-variant). */
  readonly noActivationLabel?: string;
  /**
   * Vertaal-template voor de fixatie-tooltip. De builder verzorgt de invulling
   * a.d.h.v. de gedetecteerde {@link HdGateLineFixationCause}-records.
   *
   * - `prefix`: voorvoegsel voor de tooltip — bv. `"Fixatie door"` (de spatie
   *   tussen prefix en eerste oorzaak voegt de builder zelf toe).
   * - `inThisGate(planetName)`: weergave voor een oorzaak in déze poort.
   *   Bv. `name => name` levert `"Mars"`.
   * - `inHarmonicGate(planetName, gate)`: weergave voor een oorzaak in een
   *   harmonische poort. Bv. `(name, g) => `${name} in poort ${g}`` levert
   *   `"Mars in poort 8"`.
   */
  readonly fixationCauseLabels?: HdGateLineFixationCauseLabels;
  /**
   * Informatieve posities (Chiron) van deze poort. Geen activaties: enkel een
   * markering op hun lijn, die daardoor als niet-geactiveerd getoond wordt.
   */
  readonly informativePositions?: readonly HdGateLineInformativePosition[];
  /** Hover-tekst van de informatieve markering. */
  readonly informativeLabel?: string;
}

/**
 * Vertaal-template-helpers voor de tooltip-tekst van een exaltation-/
 * detriment-fixatie. Wordt door de caller (component met Transloco-context)
 * voorzien zodat de builder zelf zuiver presentational blijft.
 */
export interface HdGateLineFixationCauseLabels {
  readonly prefix: string;
  inThisGate(planetName: string): string;
  inHarmonicGate(planetName: string, gate: number): string;
  /** Markeert een individuele oorzaak als afkomstig uit de Just Now-transit. */
  transitSource?(causeLabel: string): string;
}

/** Minimale activatie-vorm die de builder nodig heeft (subset van de chart-activatie). */
export interface HdGateLineActivationSource {
  /** Numerieke planeet-id (betrouwbare lookup-sleutel; `planet` is enkel een label). */
  readonly planetId: number;
  /** Display-label van het planeet (fallback-tooltip wanneer de id onbekend is). */
  readonly planet: string;
  readonly side: HdBodygraphSide;
  /**
   * Poort waar deze activatie staat. Optioneel: voor `gateActivations` (activaties
   * van déze poort) is dit niet nodig; voor `harmonicGatesActivations` (over
   * meerdere poorten heen) wel zodat de builder per-harmonische-poort kan groeperen.
   */
  readonly gate?: number;
  readonly line: number;
  /** Fixatie-status van deze activatie (uit `resolveHdFixingState`). */
  readonly fixing: HdFixingState;
  readonly origin?: HdGateLineActivationOrigin;
}

/**
 * Berekent de harmonische poorten van een gegeven poort: de andere kant van elk
 * kanaal waar de poort deel van uitmaakt. Een poort kan in meerdere kanalen
 * voorkomen (bv. gate 20 in `34-20`, `10-20`, `20-57`) → 3 harmonics.
 */
export function getHdHarmonicGatesOf(gate: number): readonly number[] {
  const seen = new Set<number>();
  const result: number[] = [];
  for (const channel of HD_BODYGRAPH_CHANNELS) {
    if (channel.gates[0] === gate && !seen.has(channel.gates[1])) {
      seen.add(channel.gates[1]);
      result.push(channel.gates[1]);
    } else if (channel.gates[1] === gate && !seen.has(channel.gates[0])) {
      seen.add(channel.gates[0]);
      result.push(channel.gates[0]);
    }
  }
  return result;
}

/**
 * Zet de ruwe lijn-vertaling om naar een render-klaar view-model. Pure functie:
 * geen Angular, geen services — zo blijft ze triviaal te testen en deelbaar tussen
 * de horizontale lijn-variant en de portret-card.
 *
 * Alle velden mogen leeg zijn; een exaltation/detriment zonder enige inhoud wordt
 * `null` (de UI rendert die rij dan niet). Het `planet`-veld is een planeet-key
 * ("mars", "venus", …) → de bijhorende Astronomicon-glyph en vertaalde naam; een
 * leeg of onbekend `planet` levert een lege glyph/naam zonder te crashen.
 */
export function buildHdGateLineViewModel(params: BuildHdGateLineViewModelParams): HdGateLineViewModel {
  const {
    gate,
    lineNumber,
    line,
    planetTranslations,
    iconLabels,
    voice,
    fearDetail,
    gateActivations,
    harmonicGatesActivations,
    noActivationLabel,
    fixationCauseLabels,
    informativePositions,
    informativeLabel,
  } = params;

  const learningAboutText = [line.learningAbout, line.learningAboutExtended]
    .filter(part => part.length > 0)
    .join(' ');

  const activationResult = buildLineActivations(
    lineNumber,
    gateActivations,
    planetTranslations,
    line,
  );

  const exaltationCauses = collectFixationCauses(
    line.exaltation.planet,
    lineNumber,
    gateActivations,
    harmonicGatesActivations,
    planetTranslations,
  );
  const detrimentCauses = collectFixationCauses(
    line.detriment.planet,
    lineNumber,
    gateActivations,
    harmonicGatesActivations,
    planetTranslations,
  );

  const exaltationCauseTooltip = formatFixationCauseTooltip(exaltationCauses, fixationCauseLabels);
  const detrimentCauseTooltip = formatFixationCauseTooltip(detrimentCauses, fixationCauseLabels);
  const exaltationFixingOrigins = collectFixingOrigins(exaltationCauses);
  const detrimentFixingOrigins = collectFixingOrigins(detrimentCauses);
  const transitFixing = combineFixingOrigins(exaltationFixingOrigins, detrimentFixingOrigins, 'transit');
  const activations = activationResult.activations.map(activation => ({
    ...activation,
    transitFixing: activation.origin === 'transit'
      ? subtractFixingState(transitFixing, activation.fixing)
      : transitFixing,
  }));

  const informativePlanets = (informativePositions ?? [])
    .filter(position => position.line === lineNumber)
    .map<HdGateLineInformativePlanetViewModel>(position => {
      const config = findHdChartPointDisplay(position.planetKey);
      return {
        glyphChar: config?.astronomiconChar ?? '',
        planetName: planetTranslations[position.planetKey]?.name ?? config?.label ?? position.planetKey,
        side: position.side,
      };
    });

  const icons = getHdGateIconLayout(gate)
    .flat()
    .map(key => ({
      key,
      faName: getHdBodygraphIconFaName(key),
      label: withHdFearIconDetail(key, buildIconLabel(key, iconLabels?.[key] ?? key, voice), fearDetail ?? ''),
      visualisation: getHdBodygraphIconVisualisation(key),
    }));

  return {
    gate,
    lineNumber,
    icons,
    gateLineLabel: `${gate}.${lineNumber}`,
    keynote: line.keynote,
    roleKeynote: distinctRoleKeynote(line.roleKeynote, line.keynote),
    roleKeynote2: line.roleKeynote2,
    subText: line.subText,
    learningAbout: line.learningAbout,
    learningAboutExtended: line.learningAboutExtended,
    learningAboutText,
    ownDescription: line.ownDescription,
    zodiacStart: line.zodiacStart,
    zodiacEnd: line.zodiacEnd,
    exaltation: buildDualityViewModel(line.exaltation, planetTranslations),
    detriment: buildDualityViewModel(line.detriment, planetTranslations),
    activations,
    // Een lijn met enkel een informatieve planeet (Chiron) is níet geactiveerd:
    // gedimd lijnnummer + geen-activatie-icoon, met de markering ernaast.
    inactiveInChart: activationResult.inactiveInChart || (informativePlanets.length > 0 && activations.length === 0),
    noActivationLabel: noActivationLabel ?? '',
    hasExaltationFixing: exaltationCauses.length > 0,
    hasDetrimentFixing: detrimentCauses.length > 0,
    exaltationCauses,
    detrimentCauses,
    exaltationCauseTooltip,
    detrimentCauseTooltip,
    exaltationFixingOrigins,
    detrimentFixingOrigins,
    informativePlanets,
    informativeLabel: informativeLabel ?? '',
  };
}

/**
 * Verzamelt de oorzaken van een exaltation- of detriment-fixatie voor één lijn.
 * Een oorzaak ontstaat wanneer de fixatie-planeet (bv. `line.exaltation.planet`)
 * in de chart staat:
 *  - op déze poort + deze lijn (cause in this gate), of
 *  - in een harmonische poort op om het even welke lijn (cause in harmonic gate).
 *
 * Lege {@link fixationPlanetKey} of geen chart-context → lege array.
 */
function collectFixationCauses(
  fixationPlanetKey: string,
  lineNumber: number,
  gateActivations: readonly HdGateLineActivationSource[] | undefined,
  harmonicGatesActivations: readonly HdGateLineActivationSource[] | undefined,
  planetTranslations: Readonly<Partial<Record<HdChartPointKey, HdPlanetTranslations>>>,
): readonly HdGateLineFixationCause[] {
  if (!fixationPlanetKey) {
    return [];
  }
  const causes: HdGateLineFixationCause[] = [];

  for (const activation of gateActivations ?? []) {
    if (activation.line !== lineNumber) continue;
    if (activation.side !== 'personality' && activation.side !== 'design') continue;
    const config = getHdPlanetDisplayConfigByPlanetId(activation.planetId);
    if (config?.key !== fixationPlanetKey) continue;
    causes.push({
      planetName: planetTranslations[config.key]?.name ?? config.label,
      side: activation.side,
      harmonicGate: null,
      ...(activation.origin ? { origin: activation.origin } : {}),
    });
  }

  for (const activation of harmonicGatesActivations ?? []) {
    if (activation.side !== 'personality' && activation.side !== 'design') continue;
    const config = getHdPlanetDisplayConfigByPlanetId(activation.planetId);
    if (config?.key !== fixationPlanetKey) continue;
    if (activation.gate == null) continue;
    causes.push({
      planetName: planetTranslations[config.key]?.name ?? config.label,
      side: activation.side,
      harmonicGate: activation.gate,
      ...(activation.origin ? { origin: activation.origin } : {}),
    });
  }

  return causes;
}

function formatFixationCauseTooltip(
  causes: readonly HdGateLineFixationCause[],
  labels: HdGateLineFixationCauseLabels | undefined,
): string {
  if (causes.length === 0 || !labels) {
    return '';
  }
  const parts = causes.map(cause => {
    const causeLabel = cause.harmonicGate == null
      ? labels.inThisGate(cause.planetName)
      : labels.inHarmonicGate(cause.planetName, cause.harmonicGate);
    return cause.origin === 'transit' && labels.transitSource
      ? labels.transitSource(causeLabel)
      : causeLabel;
  });
  return `${labels.prefix} ${parts.join(', ')}`;
}

/**
 * Filtert de poort-activaties op de huidige lijn en mapt ze naar glyph + naam.
 * `inactiveInChart` is `true` wanneer de poort wél activaties heeft maar déze
 * lijn niet. Highlight-activaties tellen niet mee.
 *
 * De `fixing`-status per activatie wordt afgeleid uit een directe vergelijking
 * tussen de activerende planeet en de exaltation-/detriment-planeet van déze
 * lijn (zie spec: "het pijltje verschijnt enkel wanneer de activerende planeet
 * overeenkomt met de planeet die bij de exaltation/detriment staat").
 */
function buildLineActivations(
  lineNumber: number,
  gateActivations: readonly HdGateLineActivationSource[] | undefined,
  planetTranslations: Readonly<Partial<Record<HdChartPointKey, HdPlanetTranslations>>>,
  line: HdGateLineTranslation,
): {
  activations: readonly HdGateLineActivationViewModel[];
  inactiveInChart: boolean;
} {
  const relevant = (gateActivations ?? []).filter(
    activation => activation.side === 'personality' || activation.side === 'design',
  );
  const gateHasActivations = relevant.length > 0;

  const activations = relevant
    .filter(activation => activation.line === lineNumber)
    .map<HdGateLineActivationViewModel>(activation => {
      const config = getHdPlanetDisplayConfigByPlanetId(activation.planetId);
      const planetKey = config?.key ?? null;
      const fixing = resolveHdPlanetLineFixing(planetKey, {
        exaltationPlanet: line.exaltation.planet,
        detrimentPlanet: line.detriment.planet,
      });
      return {
        glyphChar: config?.astronomiconChar ?? '',
        planetName: config ? planetTranslations[config.key]?.name ?? config.label : activation.planet,
        side: activation.side as 'personality' | 'design',
        fixing,
        transitFixing: 'none',
        origin: activation.origin ?? 'chart',
      };
    });

  return {
    activations,
    inactiveInChart: gateHasActivations && activations.length === 0,
  };
}

function collectFixingOrigins(
  causes: readonly HdGateLineFixationCause[],
): readonly HdGateLineActivationOrigin[] {
  const origins = new Set<HdGateLineActivationOrigin>();
  for (const cause of causes) origins.add(cause.origin ?? 'chart');
  return [...origins];
}

function combineFixingOrigins(
  exaltationOrigins: readonly HdGateLineActivationOrigin[],
  detrimentOrigins: readonly HdGateLineActivationOrigin[],
  origin: HdGateLineActivationOrigin,
): HdFixingState {
  const hasExaltation = exaltationOrigins.includes(origin);
  const hasDetriment = detrimentOrigins.includes(origin);
  if (hasExaltation && hasDetriment) return 'both';
  if (hasExaltation) return 'exaltation';
  return hasDetriment ? 'detriment' : 'none';
}

/** Voorkomt dubbele groene pijlen wanneer de transitactivatie zelf de oorzaak is. */
function subtractFixingState(source: HdFixingState, alreadyShown: HdFixingState): HdFixingState {
  const hasExaltation = (source === 'exaltation' || source === 'both')
    && alreadyShown !== 'exaltation' && alreadyShown !== 'both';
  const hasDetriment = (source === 'detriment' || source === 'both')
    && alreadyShown !== 'detriment' && alreadyShown !== 'both';
  if (hasExaltation && hasDetriment) return 'both';
  if (hasExaltation) return 'exaltation';
  return hasDetriment ? 'detriment' : 'none';
}

/**
 * Verrijkt het `voice`-icoon-label met de specifieke stem van de poort, bv.
 * "Stem - Ik weet | volharden, aandringen / ophouden". Andere iconen (of een
 * voice-poort zonder stem-data) krijgen het ongewijzigde basis-label terug.
 */
function buildIconLabel(
  key: HdBodygraphIconKey,
  baseLabel: string,
  voice: HdGateVoice | null | undefined,
): string {
  if (key !== 'voice' || !voice) {
    return baseLabel;
  }
  const detail = [voice.keynote, voice.properties].filter(part => part.length > 0).join(' | ');
  return detail ? `${baseLabel} - ${detail}` : baseLabel;
}

/**
 * Verrijkt de tooltip van een angst-icoon (`fear-*`) met de specifieke angst
 * van de poort (keynotes + levensthema) op een nieuwe regel, zodat je de
 * basis al ziet zonder door te klikken. Andere iconen blijven ongewijzigd.
 */
export function withHdFearIconDetail(key: HdBodygraphIconKey, baseLabel: string, fearDetail: string): string {
  if (!key.startsWith('fear-') || !fearDetail) {
    return baseLabel;
  }
  return baseLabel ? `${baseLabel}\n${fearDetail}` : fearDetail;
}

/**
 * De rol-keynote (rolnaam uit The 36 Roles) staat in de data op elke lijn van de
 * rol-poorten, maar bij o.a. poort 7, 10 en 13 ís de I'Ching-keynote al de rolnaam
 * ("De martelaar" / "De Martelaar", "Authoritarian" / "The Authoritarian"). Een
 * cursieve herhaling onder de titel voegt dan niets toe, dus die laten we weg.
 * Vergelijking zonder hoofdletters en zonder een voorafgaand lidwoord; andere
 * woordvormen ("Autoritair" / "De Autoritaire") blijven bewust staan.
 */
function distinctRoleKeynote(roleKeynote: string, keynote: string): string {
  const normalize = (value: string) => value.trim().toLowerCase().replace(/^(de|het|een|the|a|an)\s+/, '');
  return roleKeynote && normalize(roleKeynote) === normalize(keynote) ? '' : roleKeynote;
}

/** Bouwt een exaltation/detriment-rij, of `null` wanneer geen enkel veld gevuld is. */
function buildDualityViewModel(
  duality: HdGateExaltationOrDetriment,
  planetTranslations: Readonly<Partial<Record<HdChartPointKey, HdPlanetTranslations>>>,
): HdGateLineDualityViewModel | null {
  const hasContent = Boolean(
    duality.planet || duality.keynoteWhite || duality.keynoteBlack || duality.customInterpretation
      || duality.ownInterpretation,
  );
  if (!hasContent) {
    return null;
  }

  const config = findPlanetConfig(duality.planet);
  return {
    glyphChar: config?.astronomiconChar ?? '',
    planetName: config ? planetTranslations[config.key]?.name ?? config.label : '',
    keynoteWhite: duality.keynoteWhite,
    keynoteBlack: duality.keynoteBlack,
    customInterpretation: duality.customInterpretation,
    ownInterpretation: duality.ownInterpretation,
  };
}

/** Veilige planeet-lookup: `getHdPlanetDisplayConfig` throwt op onbekende keys, deze niet. */
function findPlanetConfig(planet: string): HdPlanetDisplayConfig | undefined {
  return HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON.find(config => config.key === planet);
}
